import { useState } from 'react'
import { Smartphone, RefreshCw, AlertCircle, CheckCircle, XCircle } from 'lucide-react'
import { apiService } from '../../services/api'

export default function BulkWalletPassUpdate() {
  const [competitionYear, setCompetitionYear] = useState(new Date().getFullYear().toString())
  const [updateReason, setUpdateReason] = useState('')
  const [updating, setUpdating] = useState(false)
  const [result, setResult] = useState<{
    success: boolean
    summary?: {
      totalUsers: number
      successful: number
      failed: number
      totalDevicesNotified: number
    }
    error?: string
  } | null>(null)

  const handleBulkUpdate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!confirm(
      `Trigger wallet pass updates for all users in ${competitionYear}?\n\n` +
      `This will send push notifications to all registered devices.\n\n` +
      `Reason: ${updateReason || 'No reason provided'}`
    )) {
      return
    }

    setUpdating(true)
    setResult(null)

    const response = await apiService.triggerWalletPassUpdate({
      competitionYear,
      updateReason: updateReason || 'Bulk update triggered by admin'
    })

    if (response.error) {
      setResult({
        success: false,
        error: response.error.message
      })
    } else {
      // Backend wraps response in { data: { message, summary }, requestId, timestamp }
      const responseData = response.data as any
      const summary = responseData?.data?.summary || responseData?.summary
      setResult({
        success: true,
        summary
      })
    }

    setUpdating(false)
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-2 mb-4">
        <Smartphone className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-bold text-gray-900">Bulk Wallet Pass Update</h3>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Send push notifications to all users with wallet passes for a specific competition year.
        This will trigger pass updates on all registered devices.
      </p>

      <form onSubmit={handleBulkUpdate} className="space-y-4">
        <div>
          <label htmlFor="competition-year" className="block text-sm font-medium text-gray-700 mb-1">
            Competition Year *
          </label>
          <input
            id="competition-year"
            type="text"
            required
            value={competitionYear}
            onChange={(e) => setCompetitionYear(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="2025"
          />
        </div>

        <div>
          <label htmlFor="update-reason" className="block text-sm font-medium text-gray-700 mb-1">
            Update Reason (optional)
          </label>
          <textarea
            id="update-reason"
            value={updateReason}
            onChange={(e) => setUpdateReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., Bus group assignments updated, Emergency contact information changed"
          />
          <p className="text-xs text-gray-500 mt-1">
            This message will be shown in Google Wallet notifications
          </p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <p className="font-medium">Important Notes:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Apple Wallet: Sends silent push notifications (no user alert)</li>
              <li>Google Wallet: Limited to 3 notifications per pass per 24 hours</li>
              <li>Users must have added the pass to their wallet to receive updates</li>
            </ul>
          </div>
        </div>

        <button
          type="submit"
          disabled={updating}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {updating ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              Triggering Updates...
            </>
          ) : (
            <>
              <Smartphone className="h-4 w-4" />
              Trigger Bulk Update
            </>
          )}
        </button>
      </form>

      {/* Result Display */}
      {result && (
        <div className={`mt-4 p-4 rounded-lg ${
          result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-start gap-2">
            {result.success ? (
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              {result.success && result.summary ? (
                <div className="space-y-2">
                  <p className="font-medium text-green-800">
                    Bulk update completed successfully!
                  </p>
                  <div className="text-sm text-green-700 space-y-1">
                    <div className="grid grid-cols-2 gap-2">
                      <div>Total Users: {result.summary.totalUsers}</div>
                      <div>Successful: {result.summary.successful}</div>
                      <div>Failed: {result.summary.failed}</div>
                      <div>Devices Notified: {result.summary.totalDevicesNotified}</div>
                    </div>
                  </div>
                  {result.summary.failed > 0 && (
                    <p className="text-xs text-green-600 mt-2">
                      Some updates failed. Check CloudWatch logs for details.
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="font-medium text-red-800">Update failed</p>
                  <p className="text-sm text-red-700 mt-1">{result.error}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
